import { chatAiApi } from '../index';
import { useAuthStore, useGlobalStore } from '@/stores';
import { configSettings } from '@/lib/config';

export interface AskStanleyRequest {
    question: string;
    chat_id: string | number;
    user_id?: string | number;
    files?: File[];
}

export interface AskStanleyResponse {
    type: 'marker' | 'status' | 'response' | 'product' | 'suggestions' | 'links' | 'code-block' | 'display';
    event?: string;
    content: string | any | Array<any>;
}

/**
 * Send a message to Ask Stanley chat
 * Uses streaming response for real-time updates
 */
export async function sendAskStanleyMessage(
    data: AskStanleyRequest,
    onChunk: (chunk: AskStanleyResponse) => void,
    signal?: AbortSignal
): Promise<void> {
    const formData = new FormData();
    formData.append('question', data.question);
    formData.append('chat_id', data.chat_id.toString());

    if (data.user_id) {
        formData.append('user_id', data.user_id.toString());
    }

    if (data.files) {
        data.files.forEach(file => {
            formData.append('files', file);
        });
    }

    // Get auth headers from axios instance interceptor
    const token = useAuthStore.getState().token;
    const UUID = useGlobalStore.getState().UUID;
    const sessionUUID = useGlobalStore.getState().sessionUUID;
    const getDeviceToken = useGlobalStore.getState().getDeviceToken;

    const headers: Record<string, string> = {
        'x-request-id': UUID,
        'x-session-uuid': sessionUUID,
        'x-session-id': getDeviceToken(),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Use native fetch for streaming (axios doesn't support streaming in browser)
    const response = await fetch(`${configSettings.chatAIApiUrlV2}/v2/chat-ai/ask-ndjson`, {
        method: 'POST',
        headers,
        body: formData,
        signal,
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
        throw new Error('Response body is null');
    }

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Split by newlines to get individual JSON objects
            const lines = buffer.split('\n');

            // Keep the last incomplete line in the buffer
            buffer = lines.pop() || '';

            // Process complete lines
            for (const line of lines) {
                if (line.trim()) {
                    try {
                        const jsonData = JSON.parse(line);
                        onChunk(jsonData);
                    } catch (e) {
                        console.warn('Failed to parse chunk:', line, e);
                    }
                }
            }
        }

        // Process any remaining data in buffer
        if (buffer.trim()) {
            try {
                const jsonData = JSON.parse(buffer);
                onChunk(jsonData);
            } catch (e) {
                console.warn('Failed to parse final chunk:', buffer, e);
            }
        }
    } finally {
        reader.releaseLock();
    }
}

/**
 * Give feedback on a message
 */
export async function giveAskStanleyFeedback(messageId: string | number, feedback: number): Promise<void> {
    await chatAiApi.post('/v2/chat-ai/give-feedback', {
        response_id: messageId,
        feedback_status: feedback,
    });
}
